import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/all";
import { useEffect, useRef, useState } from "react";
// import AnimatedTitle from "./AnimatedTitle";
import ShinyText from "./ShinyText";
import MagicBento from "./MagicBento";
import FullscreenButton from "./FullscreenButton";
import "./about.css"; // Add this import

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const aboutRef = useRef(null);
  const videoRef = useRef(null);

  // Intersection Observer to detect when About section comes into view
  useEffect(() => {
    const currentRef = aboutRef.current; // Capture ref value
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !shouldLoadVideo) {
          console.log('About section in view, loading video...');
          setShouldLoadVideo(true);
        }
      },
      { 
        threshold: 0.1, // Trigger when 10% of the section is visible
        rootMargin: '100px' // Start loading 100px before the section comes into view
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [shouldLoadVideo]);

  useGSAP(() => {
    // Add slight delay to ensure other ScrollTriggers are set up first
    const timer = setTimeout(() => {
      // Refresh ScrollTrigger to ensure proper calculations
      ScrollTrigger.refresh();
    }, 100);
    
    // Welcome text animation - fade up
    gsap.fromTo(
      ".welcome-text",
      {
        opacity: 0,
        y: 30,
      },
      {
        opacity: 1,
        y: 0,
        scrollTrigger: {
          trigger: ".welcome-text",
          start: "top 90%",
          end: "top 30%",
          scrub: true,
        }
      }
    );


    const clipAnimation = gsap.timeline({
      scrollTrigger: {
        trigger: "#clip",
        start: "center center",
        end: "+=500 center",
        scrub: 0.5,
        pin: true,
        pinSpacing: true,
        refreshPriority: -1,
        onUpdate: (self) => {
          // Trigger Features preload when About animation is 80% complete
          if (self.progress > 0.8 && !window.featuresPreloadTriggered) {
            window.featuresPreloadTriggered = true;
            console.log('[About] Animation 80% complete, triggering Features preload');
            window.dispatchEvent(new CustomEvent('preloadFeatures'));
          }
          // Reset flag when scrolling back up
          if (self.progress < 0.7) {
            window.featuresPreloadTriggered = false;
          }
        }
      },
    });

    // Use fromTo to explicitly set start and end values
    clipAnimation.fromTo(
      ".mask-clip-path",
      {
        width: window.innerWidth < 500 ? "50vw" : "40vw", // Wider on mobile
        height: window.innerWidth < 500 ? "60vh" : window.innerHeight < 700 ? "45vh" : "60vh", // Smaller height on mobile
        borderRadius: "1.5rem",
      },
      {
        width: "100vw",
        height: "120vh",
        borderRadius: 0,
        ease: "power2.inOut",
      }
    );

    // This makes the text fade out as video scales - less aggressive
    clipAnimation.fromTo(
      ".video-bottom-text",
      {
        opacity: 1,
        y: 0,
      },
      {
        opacity: 0.2, // Don't fade out completely
        y: -20, // Less dramatic movement
        duration: 0.3, // Shorter duration
      },
      0.7 // Start later in the animation
    );

    // Cleanup timer
    return () => {
      clearTimeout(timer);
    };
  });

  const handleVideoLoad = () => {
    console.log('About video loaded successfully');
  };

  const handleVideoError = (e) => {
    console.error('About video failed to load:', e);
  };

  return (
    <div ref={aboutRef} id="about" className="min-h-screen w-screen">
      <div className="relative mb-8 mt-36 flex flex-col items-center gap-5">
        <p className="welcome-text font-circular-web text-sm uppercase md:text-[16px]">
          Welcome to TheLogicLoom
        </p>

        <ShinyText 
          text="The Birth of Lux" 
          className="mt-5 text-center font-zentry text-7xl font-black uppercase tracking-[0.035em] text-black md:text-[6rem]"
          speed={3}
          as="h2"
        />

        <MagicBento 
          textAutoHide={true}
          enableStars={true}
          enableSpotlight={true}
          enableBorderGlow={true}
          enableTilt={true}
          enableMagnetism={true}
          clickEffect={true}
          spotlightRadius={300}
          particleCount={12}
          glowColor="132, 0, 255"
        />
      </div>

      <div className="relative h-dvh w-screen" id="clip">
        <div className="mask-clip-path about-image">
          {shouldLoadVideo ? (
            <video
              ref={videoRef}
              src="https://storage.googleapis.com/tll-videos-for-web/BirthOfLux_V2_CC.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="absolute left-0 top-0 size-full object-cover"
              disablePictureInPicture={true}
              controlsList="nodownload noremoteplayback"
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
            >
              <track
                kind="captions"
                src="/captions/birth-of-lux-captions.vtt"
                srcLang="en"
                label="English captions"
                default
              />
            </video>
          ) : (
            // Placeholder while video isn't loaded - could be a low-res image or poster
            <div className="absolute left-0 top-0 size-full bg-gray-900" />
          )}
          
          {/* Fullscreen button */}
          <FullscreenButton 
            videoRef={videoRef}
            isVisible={shouldLoadVideo}
            className="absolute bottom-8 right-8 z-50"
          />
        </div>

        {/* Text centered in bottom area - improved mobile positioning */}
        <div className="video-bottom-text absolute inset-x-0 bottom-[10%] z-10 flex justify-center md:bottom-[10%]">
          <div className="video-text-content w-full max-w-lg px-4 text-center font-circular-web text-lg md:max-w-2xl">
            <p className="mb-2">Visual thinking builds better systems</p>
            <p className="text-gray-500">
            Whether it&apos;s building reusable component libraries that automate UI patterns across applications
            or creating API architectures that seamlessly sync front-end interactions with complex database operations,
            a similar method applies: view the entire stack from a bird&apos;s eye view, then engineer automated solutions
            that integrate front-end experiences, back-end logic, and data persistence into one cohesive system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;